import { apiClient } from '@borradh-workspace/api-client';
import type { QueryConfig } from '@borradh-workspace/api-client/react-query';
import {
  type ChannelStatusResponse,
  channelStatusResponseSchema,
} from '@borradh-workspace/contracts';
import { queryOptions, useQuery } from '@tanstack/react-query';

import { queryKeys } from '@/lib/query-keys';

/**
 * Per-channel status: Facebook posting, Instagram posting, Instagram chatbot,
 * Messenger and WhatsApp, each judged on the record its code path uses.
 *
 * Claire's `integrations_getChannelStatus` reads the same endpoint, so the
 * "Channel status" list in Settings and what Claire tells the owner cannot
 * disagree about which Instagram connection is missing (ENG-1944).
 */
export const getChannelStatusQueryOptions = () =>
  queryOptions({
    queryKey: queryKeys.integrations.channelStatus(),
    queryFn: () =>
      apiClient.get<ChannelStatusResponse>('integrations/channel-status', {
        schema: channelStatusResponseSchema,
      }),
    staleTime: 60 * 1000,
  });

type UseGetChannelStatusOptions = {
  queryConfig?: QueryConfig<typeof getChannelStatusQueryOptions>;
};

export const useGetChannelStatus = ({
  queryConfig,
}: UseGetChannelStatusOptions = {}) => {
  const query = useQuery({
    ...getChannelStatusQueryOptions(),
    ...queryConfig,
  });

  return {
    channels: query.data?.channels ?? [],
    isLoading: query.isPending,
    isError: query.isError,
    isRefetching: query.isRefetching,
    refetch: query.refetch,
  };
};
